import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Save, PanelRightClose, PanelRightOpen, Download, Printer } from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { MeetingTasksSidebar } from '../components/MeetingTasksSidebar';
import { MeetingInfoSidebar } from '../components/MeetingInfoSidebar';
import { useRightPanel } from '../contexts/RightPanelContext';
import { useToast } from '../contexts/ToastContext';
import { apiGet, apiPatch } from '../lib/api';
import { tiptapToMarkdown, downloadMarkdown, printAsPdf } from '../lib/export';

interface MeetingInstanceDetail {
  id: string;
  series_id: string;
  series_name: string;
  title: string | null;
  meeting_date: string;
  note_content: string | null;
  summary: string | null;
  attendee_ids: string[];
  default_attendee_ids?: string[];
  prev_instance_id: string | null;
  next_instance_id: string | null;
  created_at: string;
  updated_at: string;
}

export function MeetingInstancePage() {
  const { seriesId, instanceId } = useParams<{ seriesId: string; instanceId: string }>();
  const [instance, setInstance] = useState<MeetingInstanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const { isOpen: sidebarOpen, setIsOpen: setSidebarOpen, register, unregister } = useRightPanel();
  const toast = useToast();

  // Register this page as having a right panel
  useEffect(() => {
    register();
    return () => unregister();
  }, [register, unregister]);

  async function load() {
    if (!instanceId) return;
    setLoading(true);
    try {
      const data = await apiGet<MeetingInstanceDetail>(`/meeting-instances/${instanceId}`);
      setInstance(data);
    } catch (err) {
      console.error('Failed to load meeting instance:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [instanceId]);

  const handleNoteUpdate = useCallback(async (content: JSONContent) => {
    if (!instanceId) return;
    setSaving(true);
    try {
      await apiPatch(`/meeting-instances/${instanceId}`, {
        note_content: JSON.stringify(content),
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save notes:', err);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  }, [instanceId]);

  const handleAttendeeIdsChange = useCallback((newIds: string[]) => {
    setInstance(prev => prev ? { ...prev, attendee_ids: newIds } : prev);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  if (!instance) {
    return <div className="text-text-muted text-center py-16">Meeting not found.</div>;
  }

  const noteContent: JSONContent | null = instance.note_content
    ? (() => { try { return JSON.parse(instance.note_content); } catch { return null; } })()
    : null;

  return (
    <div className="flex h-full relative">
      {/* Main content area */}
      <div className="flex-1 min-w-0 overflow-auto p-6">
        <div>
          {/* Header row: back link | title+date centered | nav + actions */}
          <div className="flex items-center mb-4 no-print">
            {/* Left: back link */}
            <div className="flex-shrink-0">
              <Link
                to={`/meetings/${seriesId}`}
                className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
              >
                <ChevronLeft size={16} />
                {instance.series_name}
              </Link>
            </div>

            {/* Center: title + date */}
            <div className="flex-1 min-w-0 text-center">
              <h1 className="text-lg font-bold text-text-primary truncate">
                {instance.title || instance.series_name}
              </h1>
              <div className="flex items-center justify-center gap-2 text-xs text-text-muted">
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(instance.meeting_date)}
                </span>
                {saving && <span className="text-accent">Saving...</span>}
                {lastSaved && !saving && (
                  <span className="flex items-center gap-1">
                    <Save size={10} />
                    {lastSaved}
                  </span>
                )}
              </div>
            </div>

            {/* Right: prev/next + export/print */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {instance.prev_instance_id ? (
                <Link
                  to={`/meetings/${seriesId}/${instance.prev_instance_id}`}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
                >
                  <ChevronLeft size={14} /> Prev
                </Link>
              ) : (
                <span className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted/40">
                  <ChevronLeft size={14} /> Prev
                </span>
              )}
              {instance.next_instance_id ? (
                <Link
                  to={`/meetings/${seriesId}/${instance.next_instance_id}`}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
                >
                  Next <ChevronRight size={14} />
                </Link>
              ) : (
                <span className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted/40">
                  Next <ChevronRight size={14} />
                </span>
              )}
              <div className="w-px h-4 bg-border mx-1" />
              <button
                onClick={() => {
                  if (noteContent) {
                    const md = tiptapToMarkdown(noteContent);
                    const filename = (instance.title || instance.series_name).replace(/[^a-zA-Z0-9\s-]/g, '').trim();
                    downloadMarkdown(md, `${filename} - ${instance.meeting_date}`);
                  }
                }}
                className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                title="Export as Markdown"
              >
                <Download size={14} />
              </button>
              <button
                onClick={printAsPdf}
                className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                title="Print / Save as PDF"
              >
                <Printer size={14} />
              </button>
            </div>
          </div>

          {/* Editor */}
          <DossierEditor
            content={noteContent}
            onUpdate={handleNoteUpdate}
            editable={true}
            placeholder="Start taking notes for this meeting..."
          />
        </div>
      </div>

      {/* Edge tab to reopen right sidebar */}
      <button
        onClick={() => setSidebarOpen(true)}
        className={`absolute right-0 top-1 z-10 flex items-center justify-center w-6 h-8 bg-bg-secondary border border-r-0 border-border rounded-l-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-all duration-200 no-print ${
          sidebarOpen ? 'translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
        }`}
        title="Open sidebar"
      >
        <PanelRightOpen size={14} />
      </button>

      {/* Right sidebar — split into Tasks (top) and Info (bottom) */}
      <div
        className={`flex-shrink-0 border-l border-border bg-bg-secondary transition-all duration-200 no-print ${
          sidebarOpen ? 'w-[280px]' : 'w-0 overflow-hidden'
        }`}
      >
        <div className="w-[280px] h-full flex flex-col">
          {/* Close sidebar button */}
          <div className="flex items-center justify-end px-2 py-1 border-b border-border">
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
              title="Close sidebar"
            >
              <PanelRightClose size={16} />
            </button>
          </div>
          {/* Tasks — top half */}
          <div className="flex-1 min-h-0 border-b border-border overflow-y-auto">
            <MeetingTasksSidebar
              instanceId={instanceId!}
              seriesId={seriesId!}
            />
          </div>

          {/* Info — bottom half */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <MeetingInfoSidebar
              instanceId={instanceId!}
              seriesId={seriesId!}
              attendeeIds={instance.attendee_ids}
              defaultAttendeeIds={instance.default_attendee_ids}
              onAttendeeIdsChange={handleAttendeeIdsChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}
